import React, { useEffect, useMemo, useRef, useState } from 'react';

import './ScreeningAlertFilters.scss';
import {
  ApiAssignmentUser,
  ApiAttributeMapping,
  ApiNote,
  ApiSanctionAlert,
  ApiSanctionEntityType,
  ApiScreeningAlertFilter,
  ApiSortDirection,
} from '../../../api';
import {
  assignAndGoToNextScreeningAlertInFilter,
  fetchFilterMatchingScreeningAlerts,
} from './screeningAlertFiltersActions';
import UnknownSizeLoadMoreTable from '../../../table/UnknownSizeLoadMoreTable';
import FormattedDateTime from '../../../common/datetime/FormattedDateTime';
import { isNavigableCellClicked } from '../../../common/helper';
import { useDispatch } from 'react-redux';
import { getValueFromEntityByFieldKey } from '../../helper';
import ScreeningMatchesPopover from '../../ScreeningMatchesPopover';
import ScreeningEntityDetails from '../../ScreeningEntityDetails';
import SanctionAlertRowAssignment from '../../ScreeningAlertRowAssignment';
import { useNavigate } from 'react-router';
import { Spinner } from '../../../feedback/Spinner';
import { Button } from '@salvapp/ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons';
import { faPlay, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons';
import SanctionAlertStatus from '../../ScreeningAlertStatus';
import { getBulkAssignment, getBulkStatus } from '../../../common/bulk/helper';
import UserEventTracker from '../../../tracking/userEventTracker';
import { InternalApiAlertType } from '../../../api-internal';
import { sanctionAlertsBulkApi } from '../../../apis';
import { uniqBy } from 'lodash';
import { useMe } from '../../../organisation/useMe';

const getNextOffsetId = (alerts: Array<ApiSanctionAlert>, sortDirection: ApiSortDirection) => {
  if (sortDirection === ApiSortDirection.DESC) {
    return Math.min(...alerts.map((it) => it.id));
  }
  return Math.max(...alerts.map((it) => it.id));
};

const getNextOffsetCreatedTime = (alerts: Array<ApiSanctionAlert>, sortDirection: ApiSortDirection) => {
  if (sortDirection === ApiSortDirection.DESC) {
    return new Date(Math.min(...alerts.map((it) => it.createdTime.getTime())));
  }
  return new Date(Math.max(...alerts.map((it) => it.createdTime.getTime())));
};

const toScreeningAlertInfo = (alert: ApiSanctionAlert): AlertInfo => {
  return {
    id: alert.id,
    alertType: InternalApiAlertType.SCREENING,
    entity: alert.entity.entityType,
  };
};

export interface AlertInfo {
  id: number;
  alertType: InternalApiAlertType;
  entity: ApiSanctionEntityType;
}

export const ScreeningAlertFiltersResults = ({
  filter,
  mappings,
  searchParams,
  reloadFilters,
}: ScreeningAlertFiltersResultsProps): React.JSX.Element | null => {
  const BATCH_SIZE = 50;
  const navigate = useNavigate();
  const [offsetId, setOffsetId] = useState<number | undefined>();
  const [offsetCreatedTime, setOffsetCreatedTime] = useState<Date | undefined>();
  const sortDirection: ApiSortDirection =
    searchParams.get('direction') === ApiSortDirection.ASC ? ApiSortDirection.ASC : ApiSortDirection.DESC;
  const [loading, setLoading] = useState(false);
  const [previousRow, setPreviousRow] = useState<number | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);
  const reloadFiltersAndResults = async () => {
    await reloadFilters();
    setReloadCounter(reloadCounter + 1);
  };
  const currentFilterId = useRef(filter.id);

  const dispatch = useDispatch();
  const [alerts, setAlerts] = useState<ApiSanctionAlert[]>([]);
  const { currentOrganisation } = useMe();
  const [selectedAlerts, setSelectedAlerts] = useState<AlertInfo[]>([]);

  const flipSortDirection = () => {
    if (sortDirection === ApiSortDirection.DESC) {
      navigate({
        search: `?id=${filter?.id}&direction=ASC`,
      });
    } else {
      navigate({
        search: `?id=${filter?.id}&direction=DESC`,
      });
    }
  };

  const updateAlert = (updatedAlert: ApiSanctionAlert) => {
    const updatedAlerts = alerts.map((alert) => {
      if (alert.id !== updatedAlert.id) {
        return alert;
      } else {
        return { ...alert, ...updatedAlert };
      }
    });
    setAlerts(updatedAlerts);
  };

  useEffect(() => {
    currentFilterId.current = filter.id;
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    // fetch the initial batch
    fetchFilterMatchingScreeningAlerts(filter.id, sortDirection, BATCH_SIZE)
      .then((result) => {
        // If the filter ID from the request does not equal current active filter id then disregard the results
        if (result?.filterId !== currentFilterId.current) return;
        if (result?.alerts && result.alerts.length > 0) {
          // calculate the next offset if the first batch contains any items
          setOffsetId(getNextOffsetId(result.alerts, sortDirection));
          setOffsetCreatedTime(getNextOffsetCreatedTime(result.alerts, sortDirection));
        } else {
          setOffsetId(undefined);
          setOffsetCreatedTime(getNextOffsetCreatedTime(result.alerts, sortDirection));
        }
        setAlerts(result?.alerts || []);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [filter, sortDirection, reloadCounter]);

  const navigateToScreeningAlert = (id: number) => {
    navigate(`/screening/alerts/${id}`);
  };

  const fetchData = () => {
    const nextBatch = fetchFilterMatchingScreeningAlerts(
      filter.id,
      sortDirection,
      BATCH_SIZE,
      offsetId,
      offsetCreatedTime
    );
    return new Promise<void>((resolve, reject) => {
      nextBatch
        .then((result) => {
          if (result && result.alerts?.length) {
            setOffsetCreatedTime(getNextOffsetCreatedTime(result.alerts, sortDirection));
            setOffsetId(getNextOffsetId(result.alerts, sortDirection));
            setAlerts((alerts || []).concat(result.alerts));
            resolve();
          } else {
            reject();
          }
        })
        .catch((error) => reject(error));
    });
  };

  const updateBulkScreeningAlertsAssignee = async (assignee: ApiAssignmentUser) => {
    await sanctionAlertsBulkApi.updateSanctionAlertsAssignee({
      apiBulkAssignee: {
        ids: selectedAlerts.map((it) => it.id),
        assignee: assignee,
      },
    });
    reloadFilters();
    setSelectedAlerts([]);
  };

  const updateBulkScreeningAlertsStatus = async (note: ApiNote, status: string) => {
    await sanctionAlertsBulkApi.updateSanctionAlertsStatuses({
      apiBulkStatus: {
        ids: selectedAlerts.map((it) => it.id),
        note: note,
        status: status,
      },
    });
    reloadFilters();

    UserEventTracker.trackUserEvent('SCREENING_ALERT_FILTERS_STATUS_CHANGE_TABLE_BULK', {
      alertIds: selectedAlerts,
      status,
      count: selectedAlerts.length,
    });
  };

  const clearSelectedAlerts = () => {
    reloadFilters();
    setSelectedAlerts([]);
  };

  const header = (
    <tr>
      <th />
      <th>ALERT ID</th>
      <th>OBJECT</th>
      <th onClick={flipSortDirection} className={'pointer'}>
        {sortDirection === ApiSortDirection.DESC ? (
          <FontAwesomeIcon icon={faSortDown} aria-label='descending' />
        ) : (
          <FontAwesomeIcon icon={faSortUp} aria-label='ascending' />
        )}
        <span className='m-2'>ALERT TIME</span>
      </th>
      <th>MATCHES</th>
      {getBulkAssignment(selectedAlerts, updateBulkScreeningAlertsAssignee)}
      {getBulkStatus(
        selectedAlerts,
        InternalApiAlertType.SCREENING,
        updateBulkScreeningAlertsStatus,
        clearSelectedAlerts
      )}
    </tr>
  );

  const setAlertSelected = (alert: ApiSanctionAlert, index: number, selectMultiple = true) => {
    const newSelectedAlerts = [...selectedAlerts];
    const alertInfo = toScreeningAlertInfo(alert);
    const indexOfAlertId = newSelectedAlerts.map((it) => it.id).indexOf(alertInfo.id);

    if (indexOfAlertId === -1) {
      // if shift key is being pressed upon click and there is a row that was previously selected, grab all rows inbetween including both previous and current clicked rows
      if (selectMultiple && typeof previousRow === 'number' && previousRow > -1) {
        // if previous row is above current row in table
        if (previousRow < index) {
          for (let i = previousRow; i <= index; i++) {
            newSelectedAlerts.push(toScreeningAlertInfo(alerts[i]));
          }
          // or the opposite
        } else {
          for (let i = index; i <= previousRow; i++) {
            newSelectedAlerts.push(toScreeningAlertInfo(alerts[i]));
          }
        }
      } else {
        // add alert to selected alerts
        newSelectedAlerts.push(alertInfo);
      }
      setPreviousRow(index);
    } else {
      // remove alert from selected alerts
      newSelectedAlerts.splice(indexOfAlertId, 1);
      setPreviousRow(null);
    }
    // make sure distinct IDs in the array
    setSelectedAlerts(uniqBy(newSelectedAlerts, (it) => it.id));
  };

  const isAlertSelected = (id: number) => {
    return selectedAlerts.map((it) => it.id).includes(id);
  };

  const tableRows = useMemo(
    () =>
      alerts.map((alert, index) => (
        <tr
          key={`${alert.id}-${alert.status}-${alert.assignee?.id}`}
          className={`clickable-row ${isAlertSelected(alert.id) ? 'selected-row' : ''}`}
          onClick={(event: any) => {
            isNavigableCellClicked(event) && navigateToScreeningAlert(alert.id);
          }}
        >
          <td
            onClick={(event) => {
              event.stopPropagation();
              if ((alert.matches || []).length > 0) {
                setAlertSelected(alert, index, event.shiftKey);
              }
            }}
          >
            <label className='checkbox-container'>
              {(alert.matches || []).length > 0 && (
                <>
                  <input
                    data-testid='checkbox'
                    type='checkbox'
                    checked={isAlertSelected(alert.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                    }}
                  />

                  <span className='check-mark' onClick={(event) => event.stopPropagation()} />
                </>
              )}
            </label>
          </td>
          <td>{alert.id}</td>
          <td>
            <ScreeningEntityDetails
              entity={alert.entity}
              mappings={mappings}
              organisationCurrency={currentOrganisation?.currency}
            />
          </td>
          <td>
            <FormattedDateTime date={alert.createdTime} showTime />
          </td>
          <td>
            <ScreeningMatchesPopover
              originalInput={getValueFromEntityByFieldKey(alert.fieldKey, alert.entity)}
              hits={alert.matches || []}
              status={alert.status}
            />
          </td>
          <td>
            {(alert.matches || []).length > 0 && (
              <SanctionAlertRowAssignment
                alert={alert}
                onChange={(user) =>
                  updateAlert({
                    ...alert,
                    assignee: {
                      id: user.id || 0,
                      email: user.email,
                      name: user.name,
                    },
                  })
                }
              />
            )}
          </td>
          <td>
            {(alert.matches || []).length > 0 && (
              <SanctionAlertStatus alert={alert} usage={'TABLE'} btnSize='medium' onComplete={reloadFilters} />
            )}
          </td>
        </tr>
      )), [ // eslint-disable-line
      alerts,
      currentOrganisation,
      mappings,
      selectedAlerts,
    ]
  );

  return (
    <span className={'screening-alert-filters-results'}>
      <UnknownSizeLoadMoreTable
        title={filter.name}
        header={header}
        rows={tableRows}
        fetchData={fetchData}
        maxBatchSize={BATCH_SIZE}
        loading={loading}
        className='alert-filters-load-more-table'
        cardHeaderRightPane={
          <>
            {!filter.inSync && (
              <>
                <Spinner loading={true} small={true} />
                <span className='loading-more m-3'>Table loading. Refresh to see the latest results</span>
              </>
            )}
            <span title='Refresh'>
              <Button variant='white' size='small' className={'me-2'} onClick={reloadFiltersAndResults}>
                <FontAwesomeIcon icon={faArrowsRotate} aria-label='Refresh' />
              </Button>
            </span>
            <span title='Assign alert to yourself for solving'>
              <Button
                variant='white'
                size='small'
                dataTestId={'alert-flow-btn'}
                onClick={() => {
                  dispatch(assignAndGoToNextScreeningAlertInFilter(filter.id, sortDirection, navigate));
                }}
              >
                <FontAwesomeIcon icon={faPlay} aria-label='Assign alert to yourself for solving' />
              </Button>
            </span>
          </>
        }
      />
    </span>
  );
};

interface ScreeningAlertFiltersResultsProps {
  filter: ApiScreeningAlertFilter;
  mappings: { [p: string]: ApiAttributeMapping };
  searchParams: URLSearchParams;
  reloadFilters: () => unknown;
}

export default ScreeningAlertFiltersResults;
